import React, { useEffect } from 'react';
import { LocalAudioTrack } from 'twilio-video';
import { MaterialIcons } from '@iclinic/design-system';

import useLocalAudioToggle from 'features/video-conference/hooks/useLocalAudioToggle';
import ToggleButton from 'features/video-conference/components/TeamComponents/ToggleButton';
import { buttonsTitle, MICROPHONE } from 'features/video-conference/constants';
import { useConference } from 'features/video-conference/state/conferenceContext';
import { useVerifyDevicePermission } from 'features/video-conference/hooks/useVerifyDevicePermission';

const { Mic, MicOff } = MaterialIcons;
interface Props {
  withLabel?: boolean;
}

export default function MicrophoneButton({ withLabel = false }: Props) {
  const {
    localTracks,
    onError,
    getLocalAudioTrack,
    room,
    setIsMicrophoneEnable,
  } = useConference();
  const { isAudioEnabled, toggleAudioEnabled } = useLocalAudioToggle();
  const { hasPermission } = useVerifyDevicePermission({
    deviceType: 'microphone',
  });

  const twilioLocalParticipant = room?.localParticipant;
  const hasAudioTrack = localTracks.some((track) => track.kind === 'audio');

  useEffect(() => {
    if (withLabel) setIsMicrophoneEnable(isAudioEnabled);
  }, [isAudioEnabled, setIsMicrophoneEnable, withLabel]);

  const title = isAudioEnabled
    ? buttonsTitle.AUDIO_DISABLED
    : buttonsTitle.AUDIO_ENABLED;

  const handleToggleAudio = () => {
    if (!hasAudioTrack || !hasPermission) {
      getLocalAudioTrack()
        .then((track: LocalAudioTrack) =>
          twilioLocalParticipant?.publishTrack(track),
        )
        .catch(onError);
      return;
    }

    toggleAudioEnabled();
  };

  return (
    <ToggleButton
      onClick={handleToggleAudio}
      switchedOn={isAudioEnabled && hasAudioTrack && hasPermission}
      variant="error"
      updateOn={false}
      label={withLabel ? MICROPHONE : ''}
      title={title}
      icon={
        isAudioEnabled && hasAudioTrack && hasPermission ? <Mic /> : <MicOff />
      }
      dataGa="telemedicine_microphone"
      dataSp="telemedicine_microphone"
    />
  );
}
